import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText,
  Chip,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import routeService from '../../services/routeService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  stopsSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stopsTitle: {
    marginBottom: theme.spacing(1),
  },
  stopItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  stopInput: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
}));

const RouteCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    origin: '',
    destination: '',
    distance: '',
    duration: '',
    type: 'Direct',
    price: '',
    status: 'Active',
    stops: [], // Array of {name, arrivalTime, departureTime}
    description: ''
  });
  const [newStop, setNewStop] = useState({ name: '', arrivalTime: '', departureTime: '' });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleAddStop = () => {
    if (!newStop.name.trim()) {
      setErrors({
        ...errors,
        stops: 'Le nom de l\'arrêt est requis'
      });
      return;
    }
    
    // Vérifier si l'arrêt existe déjà
    if (formData.stops.some(stop => stop.name === newStop.name.trim())) {
      setErrors({
        ...errors,
        stops: 'Cet arrêt existe déjà dans la liste'
      });
      return;
    }
    
    const stopToAdd = {
      name: newStop.name.trim(),
      arrivalTime: newStop.arrivalTime || '',
      departureTime: newStop.departureTime || ''
    };
    
    setFormData({
      ...formData,
      stops: [...formData.stops, stopToAdd],
      type: formData.stops.length > 0 || newStop.name.trim() ? 'Multiple' : 'Direct'
    });
    setNewStop({ name: '', arrivalTime: '', departureTime: '' });
    
    // Clear error when stops are added
    if (errors.stops) {
      setErrors({
        ...errors,
        stops: null
      });
    }
  };

  const handleRemoveStop = (index) => {
    const updatedStops = formData.stops.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      stops: updatedStops,
      type: updatedStops.length > 0 ? 'Multiple' : 'Direct'
    });
  };

  const handleNewStopChange = (field, value) => {
    setNewStop({
      ...newStop,
      [field]: value
    });
    
    // Clear error when new stop field is edited
    if (errors.stops) {
      setErrors({
        ...errors,
        stops: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.origin.trim()) {
      newErrors.origin = 'L\'origine est requise';
    }
    
    if (!formData.destination.trim()) {
      newErrors.destination = 'La destination est requise';
    }
    
    if (formData.origin.trim() === formData.destination.trim()) {
      newErrors.destination = 'La destination doit être différente de l\'origine';
    }
    
    if (!formData.distance) {
      newErrors.distance = 'La distance est requise';
    } else if (isNaN(formData.distance) || parseFloat(formData.distance) <= 0) {
      newErrors.distance = 'La distance doit être un nombre positif';
    }
    
    if (!formData.duration) {
      newErrors.duration = 'La durée est requise';
    } else if (isNaN(formData.duration) || parseInt(formData.duration) <= 0) {
      newErrors.duration = 'La durée doit être un nombre positif';
    }
    
    if (!formData.price) {
      newErrors.price = 'Le prix est requis';
    } else if (isNaN(formData.price) || parseFloat(formData.price) < 0) {
      newErrors.price = 'Le prix doit être un nombre positif ou zéro';
    }
    
    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }
    
    if (formData.type === 'Multiple' && formData.stops.length === 0) {
      newErrors.stops = 'Au moins un arrêt est requis pour un itinéraire à arrêts multiples';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Utilisation du service API pour créer l'itinéraire
      await routeService.createRoute({
        ...formData,
        distance: parseFloat(formData.distance),
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price)
      });
      
      setSnackbar({
        open: true,
        message: 'Itinéraire créé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/routes');
      }, 1500);
      
    } catch (error) {
      console.error('Erreur lors de la création de l\'itinéraire:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de la création de l\'itinéraire',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/routes');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Créer un itinéraire
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nom de l'itinéraire"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="ex: Paris - Lyon"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="origin"
                  label="Origine"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.origin}
                  onChange={handleChange}
                  error={!!errors.origin}
                  helperText={errors.origin}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="destination"
                  label="Destination"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.destination}
                  onChange={handleChange}
                  error={!!errors.destination}
                  helperText={errors.destination}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="distance"
                  label="Distance (km)"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.distance}
                  onChange={handleChange}
                  error={!!errors.distance}
                  helperText={errors.distance}
                  inputProps={{ min: 1, step: 0.1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="duration"
                  label="Durée (minutes)"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.duration}
                  onChange={handleChange}
                  error={!!errors.duration}
                  helperText={errors.duration}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="price"
                  label="Prix (€)"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.type}>
                  <InputLabel id="type-label">Type d'itinéraire</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Type d'itinéraire"
                    required
                  >
                    <MenuItem value="Direct">Direct</MenuItem>
                    <MenuItem value="Multiple">Avec arrêts</MenuItem>
                  </Select>
                  {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                    required
                  >
                    <MenuItem value="Active">Actif</MenuItem>
                    <MenuItem value="Inactive">Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.type === 'Multiple' && (
                <Grid item xs={12}>
                  <div className={classes.stopsSection}>
                    <Typography variant="h6" className={classes.stopsTitle}>
                      Arrêts intermédiaires
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Nom de l'arrêt"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={newStop.name}
                          onChange={(e) => handleNewStopChange('name', e.target.value)}
                          error={!!errors.stops}
                          helperText={errors.stops}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Heure d'arrivée"
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="time"
                          value={newStop.arrivalTime}
                          onChange={(e) => handleNewStopChange('arrivalTime', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Heure de départ"
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="time"
                          value={newStop.departureTime}
                          onChange={(e) => handleNewStopChange('departureTime', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleAddStop}
                          disabled={!newStop.name.trim()}
                          fullWidth
                        >
                          Ajouter
                        </Button>
                      </Grid>
                    </Grid>
                    
                    <Box mt={2}>
                      {formData.stops.length > 0 ? (
                        <Grid container spacing={1}>
                          {formData.stops.map((stop, index) => (
                            <Grid item xs={12} key={index}>
                              <Box 
                                display="flex" 
                                alignItems="center" 
                                p={1} 
                                border={1} 
                                borderColor="grey.300" 
                                borderRadius={4}
                              >
                                <Box flexGrow={1}>
                                  <Typography variant="body1">
                                    <strong>{stop.name}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {stop.arrivalTime && `Arrivée: ${stop.arrivalTime}`}
                                    {stop.arrivalTime && stop.departureTime && ' | '}
                                    {stop.departureTime && `Départ: ${stop.departureTime}`}
                                    {!stop.arrivalTime && !stop.departureTime && 'Heures non spécifiées'}
                                  </Typography>
                                </Box>
                                <IconButton 
                                  size="small" 
                                  color="secondary"
                                  onClick={() => handleRemoveStop(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Aucun arrêt ajouté
                        </Typography>
                      )}
                    </Box>
                  </div>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Informations supplémentaires sur l'itinéraire..."
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
              >
                Créer
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RouteCreatePage;
